// VerificationPage.jsx
import React, { useContext, useEffect, useState } from 'react';
import { AlertCircle, Calendar } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import HtmlRenderer from 'shared-components/src/components/HtmlRenderer';
import ReCAPTCHA from '../../../components/ReCaptcha';
import {
  Page,
  Container,
  Title,
  Subtitle,
  DCard,
  DCardHeader,
  DCardTitle,
  DCardContent,
  FieldLabel,
  DateFieldWrap,
  Slash,
  DateInput,
  YearInput,
  HiddenDateInput,
  TextInput,
  HelpText,
  CaptchaBox,
  CaptchaInner,
  AgreementRow,
  ALink,
  Section,
  SmallErrorRow,
  ALabel,
  ACheckBox,
} from './styles';
import {
  AlertCard,
  OutlineButton,
  PrimaryButton,
  ProgressBar,
  ProgressFill,
} from '../style';
import { verifyToken } from '../services';
import { DisbursementsContext } from '../../../contexts/DisbursementsContext';
import {
  AgreementCheck,
  CheckErrorIcon,
  DescriptionIcon,
  IdentityIcon,
} from '../../../components/Icons';

export default function VerificationPage() {
  const navigate = useNavigate();
  const recaptchaRef = React.createRef();
  const intl = useIntl();
  const theme = useContext(ThemeContext);
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const {
    verificationToken,
    redeemToken,
    setRedeemToken,
    setRedeemTokenExpiry,
  } = useContext(DisbursementsContext);
  const [loading, setLoading] = useState(false);
  const [recaptchaSize, setRecaptchaSize] = useState('normal');

  const [formData, setFormData] = useState({
    date_of_birth: '',
    last_four_sin: '',
    agreeCardholder: false,
    agreeAuthorization: false,
    agreePrivacy: false,
    captchaCompleted: false,
  });

  const [currentException, setCurrentException] = useState(null);
  // eslint-disable-next-line
  const [errors, setErrors] = useState({ last_four_sin: '' });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 500) {
        // Adjust breakpoint as needed
        setRecaptchaSize('compact');
      } else {
        setRecaptchaSize('normal');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!verificationToken) {
      navigate('/disbursements/redeem', { replace: true });
    }
    if (redeemToken) {
      navigate('/disbursements/transfer-options', { replace: true });
    }
  }, [verificationToken, redeemToken, navigate]);

  const handleCheckboxChange = (event, field, checked) => {
    event.persist();
    setFormData(prev => ({ ...prev, [field]: checked }));
  };
  const isDateValid = dateString => {
    const parts = dateString.split('/');
    let isDateValid = false;

    if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      if (
        parts[0].length === 2 &&
        parts[1].length === 2 &&
        parts[2].length === 4
      ) {
        if (month >= 1 && month <= 12) {
          if (year >= 1900 && year <= new Date().getFullYear()) {
            const daysInMonth = new Date(year, month, 0).getDate();
            if (day >= 1 && day <= daysInMonth) isDateValid = true;
          }
        }
      }
    }

    return isDateValid;
  };
  const isFormValid = () =>
    isDateValid(formData.date_of_birth) &&
    formData.last_four_sin.length === 4 &&
    formData.agreeCardholder &&
    formData.agreeAuthorization &&
    formData.agreePrivacy &&
    formData.captchaCompleted;
  function getErrorTitle(err) {
    const code =
      err && err.error && typeof err.error.code === 'string'
        ? err.error.code
        : '';
    return code ? code.replace(/_/g, ' ').toUpperCase() : 'ERROR';
  }

  const handleVerification = async data => {
    if (isFormValid()) {
      setCurrentException(null);
      setLoading(true);
      try {
        // Convert MM/DD/YYYY to DD-MM-YYYY
        const formatDOB = dob => {
          if (!dob) return '';
          const [mm, dd, yyyy] = dob.split('/');
          return `${yyyy}-${mm}-${dd}`;
        };

        const response = await verifyToken(
          {
            date_of_birth: formatDOB(data.date_of_birth),
            last_four_sin: data.last_four_sin,
          },
          verificationToken
        );
        setRedeemToken(response.data.session_token);
        // Convert expires_at to milliseconds timestamp if provided
        setRedeemTokenExpiry(
          response.data.expires_at
            ? new Date(response.data.expires_at).getTime()
            : null
        );
        navigate('/disbursements/transfer-options');
      } catch (err) {
        // usage:
        const errorTitle = getErrorTitle(err);
        let errorMessage =
          err?.error && err?.error?.message ? err?.error?.message : null;
        switch (true) {
        case errorMessage && errorMessage.includes('1 retry'):
          errorMessage = intl.messages['verification.error.retry1'];
          break;
        case errorMessage && errorMessage.includes('2 retry'):
          errorMessage = intl.messages['verification.error.retry2'];
          break;
        default:
        }
        if (
          err?.error?.code === 'verification_failed_link_blocked' ||
          err?.error?.code === 'account_locked'
        ) {
          navigate('/disbursements/verification-failed');
        } else if (err?.error?.code === 'verification_failed') {
          setCurrentException({
            title: intl.messages['verification.error.failed'],
            message: errorMessage,
          });
        } else {
          setCurrentException({
            title: errorTitle,
            message: errorMessage,
          });
        }
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } finally {
        setLoading(false);
      }
    }
  };

  const exceptionUI =
    currentException &&
    (() => ({
      tone: 'error',
      icon: (
        <CheckErrorIcon
          width={24}
          height={24}
          fill="#D60000"
          style={{ flexShrink: 0 }}
        />
      ),
      title: currentException.title,
      message: currentException.message,
    }))();

  const dobParts = formData.date_of_birth.split('/');
  return (
    <Page>
      <ProgressBar>
        <ProgressFill className="ten" />
      </ProgressBar>

      <Container>
        {exceptionUI && (
          <AlertCard $tone={exceptionUI.tone}>
            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'flex-start',
              }}
            >
              {exceptionUI.icon}
              <div>
                <h3>{exceptionUI.title}</h3>
                <p>
                  <HtmlRenderer html={exceptionUI.message} />
                </p>
                <div style={{ marginTop: '0.75rem' }}>
                  <OutlineButton
                    $tone={exceptionUI.tone}
                    onClick={() => setCurrentException(null)}
                  >
                    {intl.messages['common.dismiss']}
                  </OutlineButton>
                </div>
              </div>
            </div>
          </AlertCard>
        )}

        <Section style={{ textAlign: 'center' }}>
          <Title>{intl.messages['verification.title']}</Title>
          <Subtitle>{intl.messages['verification.subtitle.desc']}</Subtitle>
        </Section>

        {/* Personal Info */}
        <DCard $muted>
          <DCardHeader>
            <DCardTitle>
              <IdentityIcon className="step-icon" />
              {intl.messages['verification.personal.title']}
            </DCardTitle>
          </DCardHeader>

          <DCardContent>
            {/* DOB */}
            <div style={{ marginBottom: '1.5rem' }}>
              <FieldLabel htmlFor="date_of_birth">
                {intl.messages['verification.dob']}
              </FieldLabel>

              <div style={{ position: 'relative', width: '100%' }}>
                <DateFieldWrap
                  onChange={() => {
                    if (errors.date_of_birth) {
                      setErrors(prev => ({ ...prev, date_of_birth: '' }));
                    }
                  }}
                  onBlur={e => {
                    // Only validate when focus leaves the entire container
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      const parts = formData.date_of_birth.split('/');
                      const month = parseInt(parts[0], 10);
                      const day = parseInt(parts[1], 10);
                      const year = parseInt(parts[2], 10);
                      const isMonthValid = parts[0] && !Number.isNaN(month) && month >= 1 && month <= 12;
                      const isDayValid = parts[1] && !Number.isNaN(day) && day >= 1 && day <= 31;
                      const isYearValid = parts[2] && parts[2].length === 4 && !Number.isNaN(year) && year >= 1900 && year <= new Date().getFullYear();
                      if (!isMonthValid || !isDayValid || !isYearValid) {
                        setErrors(prev => ({
                          ...prev,
                          date_of_birth: intl.messages['verification.error.dob.invalid'],
                        }));
                      } else {
                        setErrors(prev => ({ ...prev, date_of_birth: '' }));
                      }
                    }
                  }}
                >
                  <DateInput
                    id="date_of_birth-month"
                    placeholder={
                      intl.messages['verification.dob.placeholder.month']
                    }
                    maxLength={2}
                    value={dobParts[0] || ''}
                    $w="2rem"
                    $filled={!!dobParts[0]}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '');
                      if (
                        v === '' ||
                        (parseInt(v, 10) >= 0 &&
                          parseInt(v, 10) <= 12 &&
                          v.length <= 2)
                      ) {
                        const parts = formData.date_of_birth.split('/');
                        const newDate = `${v}/${parts[1] || ''}/${parts[2] ||
                          ''}`;
                        setFormData(p => ({ ...p, date_of_birth: newDate }));
                      }
                    }}
                    onBlur={e => {
                      const v = e.target.value;
                      if (v && v.length === 1 && parseInt(v, 10) >= 1) {
                        const parts = formData.date_of_birth.split('/');
                        const padded = v.padStart(2, '0');
                        setFormData(p => ({ ...p, date_of_birth: `${padded}/${parts[1] || ''}/${parts[2] || ''}` }));
                      }
                    }}
                  />
                  <Slash>/</Slash>
                  <DateInput
                    id="date_of_birth-day"
                    placeholder={
                      intl.messages['verification.dob.placeholder.date']
                    }
                    maxLength={2}
                    value={dobParts[1] || ''}
                    $w="2rem"
                    $filled={!!dobParts[1]}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '');
                      if (
                        v === '' ||
                        (parseInt(v, 10) >= 0 &&
                          parseInt(v, 10) <= 31 &&
                          v.length <= 2)
                      ) {
                        const parts = formData.date_of_birth.split('/');
                        const newDate = `${parts[0] || ''}/${v}/${parts[2] ||
                          ''}`;
                        setFormData(p => ({ ...p, date_of_birth: newDate }));
                      }
                    }}
                    onBlur={e => {
                      const v = e.target.value;
                      if (v && v.length === 1 && parseInt(v, 10) >= 1) {
                        const parts = formData.date_of_birth.split('/');
                        const padded = v.padStart(2, '0');
                        setFormData(p => ({ ...p, date_of_birth: `${parts[0] || ''}/${padded}/${parts[2] || ''}` }));
                      }
                    }}
                  />
                  <Slash>/</Slash>
                  <YearInput
                    id="date_of_birth-year"
                    placeholder={
                      intl.messages['verification.dob.placeholder.year']
                    }
                    maxLength={4}
                    value={dobParts[2] || ''}
                    $filled={!!dobParts[2]}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, '');
                      if (v.length <= 4) {
                        const parts = formData.date_of_birth.split('/');
                        const newDate = `${parts[0] || ''}/${parts[1] ||
                          ''}/${v}`;
                        setFormData(p => ({ ...p, date_of_birth: newDate }));
                      }
                    }}
                  />

                  {/* Calendar overlay */}
                  <div
                    style={{
                      marginLeft: 'auto',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Calendar
                      size={20}
                      color="#9ca3af"
                      style={{ pointerEvents: 'none', zIndex: 10 }}
                    />
                    <HiddenDateInput
                      id="hidden-date-picker"
                      type="date"
                      value={(() => {
                        const parts = formData.date_of_birth.split('/');
                        if (
                          parts.length === 3 &&
                          parts[0] &&
                          parts[1] &&
                          parts[2] &&
                          parts[2].length === 4
                        ) {
                          const month = parts[0].padStart(2, '0');
                          const day = parts[1].padStart(2, '0');
                          const year = parts[2];
                          const mn = parseInt(month, 10);
                          const dn = parseInt(day, 10);
                          const yn = parseInt(year, 10);
                          if (mn >= 1 && mn <= 12 && dn >= 1 && yn >= 1900) {
                            const dim = new Date(yn, mn, 0).getDate();
                            if (dn <= dim) return `${year}-${month}-${day}`;
                          }
                        }
                        return '';
                      })()}
                      onChange={e => {
                        if (e.target.value) {
                          const date = new Date(`${e.target.value}T00:00:00`);
                          const month = (date.getMonth() + 1)
                            .toString()
                            .padStart(2, '0');
                          const day = date
                            .getDate()
                            .toString()
                            .padStart(2, '0');
                          const year = date.getFullYear();
                          setFormData(p => ({
                            ...p,
                            date_of_birth: `${month}/${day}/${year}`,
                          }));
                        }
                      }}
                      title="Open calendar"
                    />
                  </div>
                </DateFieldWrap>
              </div>
              {errors.date_of_birth && (
                <SmallErrorRow>
                  <AlertCircle size={16} />
                  <span>{intl.messages['verification.error.dob.invalid']}</span>
                </SmallErrorRow>
              )}
            </div>

            {/* SIN */}
            <div>
              <FieldLabel htmlFor="last_four_sin">
                {intl.messages['verification.sin']}
              </FieldLabel>
              <TextInput
                id="last_four_sin"
                type="text"
                placeholder="1234"
                maxLength={4}
                value={formData.last_four_sin}
                $filled={!!formData.last_four_sin}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '');
                  setFormData(p => ({ ...p, last_four_sin: v }));
                  setErrors(prev => ({ ...prev, last_four_sin: '' }));
                }}
                onBlur={() => {
                  if (formData.last_four_sin.length < 4) {
                    setErrors(prev => ({
                      ...prev,
                      last_four_sin:
                        intl.messages['verification.error.sin.invalid'],
                    }));
                  } else {
                    setErrors(prev => ({ ...prev, last_four_sin: '' }));
                  }
                }}
              />
              {errors.last_four_sin && (
                <SmallErrorRow>
                  <AlertCircle size={16} />
                  <span>{intl.messages['verification.error.sin.invalid']}</span>
                </SmallErrorRow>
              )}
              <HelpText>{intl.messages['verification.sin.help']}</HelpText>
            </div>

            {/* Captcha */}
            <CaptchaBox>
              <CaptchaInner>
                <ReCAPTCHA
                  id="captcha"
                  ref={recaptchaRef}
                  sitekey={SITE_KEY}
                  size={recaptchaSize}
                  onChange={token => {
                    setFormData(prev => ({ ...prev, captchaCompleted: Boolean(token) }));
                  }}
                  onExpired={() => {
                    setFormData(prev => ({
                      ...prev,
                      captchaCompleted: false,
                    }));
                  }}
                  onError={() => {
                    setFormData(prev => ({ ...prev, captchaCompleted: false }));
                  }}
                />
                {!formData.captchaCompleted && (
                  <p>{intl.messages['verification.captcha.help']}</p>
                )}
              </CaptchaInner>
            </CaptchaBox>
          </DCardContent>
        </DCard>

        {/* Agreements */}
        <DCard $muted>
          <DCardHeader>
            <DCardTitle>
              <DescriptionIcon className="step-icon" />
              {intl.messages['verification.agreements.title']}
            </DCardTitle>
          </DCardHeader>
          <DCardContent>
            <div style={{ display: 'grid', gap: '1rem' }}>
              <AgreementRow>
                <ACheckBox
                  htmlFor="agreeCardholder"
                  $checked={formData.agreeCardholder}
                >
                  <AgreementCheck />
                  <input
                    type="checkbox"
                    id="agreeCardholder"
                    defaultChecked={formData.agreeCardholder}
                    onChange={e => {
                      handleCheckboxChange(
                        e,
                        'agreeCardholder',
                        e.target.checked
                      );
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      accentColor: '#005D1F',
                      marginTop: 2,
                    }}
                  />
                </ACheckBox>

                <ALabel
                  htmlFor="agreeCardholder"
                  style={{
                    color: '#374151',
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                  }}
                >
                  {
                    intl.messages['verification.agreement1'].split(
                      intl.locale === 'fr' ? 'Accord du Porteur de Carte' : 'Cardholder Agreement'
                    )[0]
                  }
                  <ALink
                    href={intl.locale === 'fr' ? theme.brand.activateCardholderAgreementFr : theme.brand.activateCardholderAgreementEn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lime-600 hover:underline font-medium"
                  >
                    {intl.messages['common.cardholder']}
                  </ALink>
                  {
                    intl.messages['verification.agreement1'].split(
                      intl.locale === 'fr' ? 'Accord du Porteur de Carte' : 'Cardholder Agreement'
                    )[1]
                  }
                </ALabel>
              </AgreementRow>

              <AgreementRow>
                <ACheckBox
                  htmlFor="agreeAuthorization"
                  $checked={formData.agreeAuthorization}
                >
                  <AgreementCheck />
                  <input
                    type="checkbox"
                    id="agreeAuthorization"
                    defaultChecked={formData.agreeAuthorization}
                    onChange={e => {
                      handleCheckboxChange(
                        e,
                        'agreeAuthorization',
                        e.target.checked
                      );
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      accentColor: '#005D1F',
                      marginTop: 2,
                    }}
                  />
                </ACheckBox>
                <ALabel
                  htmlFor="agreeAuthorization"
                  style={{
                    color: '#374151',
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                  }}
                >
                  {intl.messages['verification.agreement2']}
                </ALabel>
              </AgreementRow>

              <AgreementRow>
                <ACheckBox
                  htmlFor="agreePrivacy"
                  $checked={formData.agreePrivacy}
                >
                  <AgreementCheck />
                  <input
                    type="checkbox"
                    id="agreePrivacy"
                    defaultChecked={formData.agreePrivacy}
                    onChange={e => {
                      handleCheckboxChange(e, 'agreePrivacy', e.target.checked);
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      accentColor: '#005D1F',
                      marginTop: 2,
                    }}
                  />
                </ACheckBox>
                <ALabel
                  htmlFor="agreePrivacy"
                  style={{
                    color: '#374151',
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                  }}
                >
                  {
                    intl.messages['verification.agreement3'].split(
                      intl.locale === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'
                    )[0]
                  }
                  <ALink
                    href={intl.locale === 'fr' ? theme.brand.activatePrivacyPolicyFr : theme.brand.activatePrivacyPolicyEn}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lime-600 hover:underline font-medium"
                  >
                    {intl.messages['common.privacy']}
                  </ALink>
                  {
                    intl.messages['verification.agreement3'].split(
                      intl.locale === 'fr' ? 'Politique de Confidentialité' : 'Privacy Policy'
                    )[1]
                  }
                </ALabel>
              </AgreementRow>
            </div>
          </DCardContent>
        </DCard>

        {/* Submit */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <PrimaryButton
            disabled={loading || !isFormValid()}
            onClick={() => {
              if (isFormValid()) {
                handleVerification(formData);
              }
            }}
          >
            {intl.messages['verification.button']}
          </PrimaryButton>
          {!isFormValid() && (
            <HelpText>{intl.messages['verification.button.help']}</HelpText>
          )}
        </div>
      </Container>
    </Page>
  );
}
